import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../config';
import { errorMessage } from '../app/errors';
import { log } from './fatalBridge';
import { buildReport } from '../harness/scenario';
import { buildBundle, copyBundleText, downloadBundle, shareBundle } from '../harness/exportResults';
import { destroyDb, openDb, resetAll } from '../data/harnessDb';
import { SEED_PRESETS, currentSeedProfile, pickTargetHypothesisId, seedDatabase } from '../harness/seed';
import { beginLiveTapRun, finalizeLiveTapRun, runSession } from '../harness/session';
import type { LiveTapLease } from '../harness/session';
import { deleteRun, loadRuns } from '../harness/resultsStore';
import { probeStorage } from '../harness/storageProbe';
import { ReportHeader, ResultsView } from './ResultsView';
import { LiveTapPad } from './LiveTapPad';
import type { Pacing } from '../harness/runner';
import type { RunCondition, RunRecord, SeedProfile, TrialSample } from '../types';

/**
 * Build unit U-0's harness, on one screen.
 *
 * Written for a specific moment: Andrew is at a touchline, one-handed, in sunlight, with no laptop
 * and no devtools. So there is no navigation, no tabs, no modal. Setup at the top, three run
 * buttons, results below, export at the bottom. Every control is ≥ 60 pt (NFR-3) and every failure
 * lands on screen in words rather than in a console he cannot open (NFR-8).
 *
 * BUILD-UNIT NOTE (U-1): moved out of `App.tsx` unchanged. `App.tsx` is now a shell that switches
 * between this and the U-1 audio spike, because both harnesses must be reachable from the **same
 * installed app** — Andrew runs them in one device session and there is no second deployable.
 */

type Phase = 'boot' | 'idle' | 'seeding' | 'running' | 'liveTap';

const SEED_LABELS: Record<string, string> = {
  empty: 'Empty — fastest, least realistic',
  typical: `Typical mid-season — ${config.SEED_MATCHES_DEFAULT} matches × ${config.SEED_OBSERVATIONS_PER_MATCH_DEFAULT}`,
  fullSeason: 'Full season — 40 matches × 600',
};

export function LatencyHarness() {
  const [phase, setPhase] = useState<Phase>('boot');
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [seed, setSeed] = useState<SeedProfile | null>(null);
  const [status, setStatus] = useState('Opening database…');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [seedPreset, setSeedPreset] = useState<keyof typeof SEED_PRESETS>('typical');
  const [audioBulkMb, setAudioBulkMb] = useState(0);
  const [trials, setTrials] = useState(config.HARNESS_TRIALS_DEFAULT);
  const [pacing, setPacing] = useState<Pacing>('burst');
  const [soakSeconds, setSoakSeconds] = useState(config.HARNESS_THERMAL_SOAK_S);
  const [tapTarget, setTapTarget] = useState<{ hypothesisId: string; matchId: string } | null>(null);

  const stopRef = useRef(false);
  // The live-tap pad is open-ended, so its lock lease outlives any single callback and has to be
  // released on every exit path — finish, discard, and unmount. A leaked lease would wedge the
  // instrument with no way out short of relaunching the app.
  const tapLeaseRef = useRef<LiveTapLease | null>(null);

  const releaseTapLease = useCallback(() => {
    tapLeaseRef.current?.release();
    tapLeaseRef.current = null;
  }, []);

  useEffect(() => releaseTapLease, [releaseTapLease]);

  const report = useMemo(() => (runs.length > 0 ? buildReport(runs, Date.now()) : null), [runs]);

  const refresh = useCallback(async () => {
    setRuns(await loadRuns());
    setSeed(await currentSeedProfile());
  }, []);

  // The OBS-1 global-handler bridge is installed once by `App.tsx`, not here: it is a single
  // process-wide sink, and re-claiming it on every screen mount would leave it unset the moment a
  // harness unmounts — which is exactly when an unhandled rejection is most likely.
  useEffect(() => {
    void (async () => {
      try {
        await openDb(() => performance.now());
        await refresh();
        setStatus('Ready');
        setPhase('idle');
      } catch (cause) {
        setError(errorMessage(cause));
        setStatus('Could not open the database');
        setPhase('idle');
      }
    })();
  }, [refresh]);

  const guard = useCallback(
    async (label: string, work: () => Promise<void>) => {
      setError(null);
      setNotice(null);
      try {
        await work();
      } catch (cause) {
        log.exception('ui', `${label} failed`, cause);
        setError(`${label} failed — ${errorMessage(cause)}`);
      }
    },
    [],
  );

  const doSeed = () =>
    guard('Seeding', async () => {
      setPhase('seeding');
      setProgress(0);
      const preset = SEED_PRESETS[seedPreset];
      if (!preset) throw new Error(`Unknown seed preset "${seedPreset}"`);
      const profile = await seedDatabase({
        ...preset,
        bulkAudioBytes: audioBulkMb * 1024 * 1024,
        onProgress: (fraction, label) => {
          setProgress(fraction);
          setStatus(label);
        },
      });
      setSeed(profile);
      setStatus('Seeded');
      setPhase('idle');
    });

  const doRun = (condition: RunCondition) =>
    guard(`${condition} run`, async () => {
      stopRef.current = false;
      setPhase('running');
      setProgress(0);
      await runSession({
        condition,
        trials,
        pacing,
        soakSeconds,
        requestPersist: true,
        onPhase: (p, detail) => setStatus(detail ? `${p} — ${detail}` : p),
        onProgress: (done, total) => setProgress(done / total),
        shouldStop: () => stopRef.current,
      });
      await refresh();
      setStatus('Run complete');
      setPhase('idle');
    });

  const startLiveTap = () =>
    guard('Live tap', async () => {
      const hypothesisId = await pickTargetHypothesisId();
      if (!hypothesisId) throw new Error('Seed the database first.');
      // Claimed before the pad mounts: a live-tap session is a run, and no other run may overlap it.
      tapLeaseRef.current = beginLiveTapRun();
      setTapTarget({ hypothesisId, matchId: hypothesisId.replace(/^h-(\d+)-\d+$/, 'm-$1') });
      setPhase('liveTap');
      setStatus('Tap the pad');
    });

  const finishLiveTap = useCallback(
    (samples: TrialSample[], failures: { index: number; message: string }[]) => {
      void guard('Live tap', async () => {
        const lease = tapLeaseRef.current;
        if (!lease) throw new Error('The live-tap session has no lock lease; start it again.');
        try {
          const record = await finalizeLiveTapRun(lease, 'warm', samples, failures, false);
          await refresh();
          setStatus(
            record.samples.length < config.HARNESS_TRIALS_MIN
              ? `Live tap saved — ${record.samples.length} taps, below the ${config.HARNESS_TRIALS_MIN}-tap floor, so not evidence`
              : 'Live tap complete',
          );
        } finally {
          releaseTapLease();
          setTapTarget(null);
          setPhase('idle');
        }
      });
    },
    [guard, refresh, releaseTapLease],
  );

  const cancelLiveTap = useCallback(() => {
    releaseTapLease();
    setTapTarget(null);
    setPhase('idle');
    setStatus('Live tap discarded');
  }, [releaseTapLease]);

  const doExport = () =>
    guard('Export', async () => {
      if (runs.length === 0) throw new Error('There is nothing to export yet.');
      const route = await shareBundle(buildBundle(runs, Date.now()));
      setNotice(route === 'download' ? 'Downloaded.' : 'Shared.');
    });

  const doCopy = () =>
    guard('Copy', async () => {
      if (runs.length === 0) throw new Error('There is nothing to copy yet.');
      await copyBundleText(buildBundle(runs, Date.now()));
      setNotice('Summary copied to the clipboard.');
    });

  const doDownload = () =>
    guard('Download', async () => {
      if (runs.length === 0) throw new Error('There is nothing to download yet.');
      downloadBundle(buildBundle(runs, Date.now()));
      setNotice('Downloaded.');
    });

  const doProbeOnly = () =>
    guard('Storage probe', async () => {
      const probe = await probeStorage(true);
      setNotice(
        probe.supported
          ? `usage ${probe.usageBytes} B / quota ${probe.quotaBytes} B · persisted=${String(probe.persistedAfter)}`
          : (probe.error ?? 'unsupported'),
      );
    });

  const doClearData = () =>
    guard('Clear', async () => {
      await resetAll();
      await refresh();
      setStatus('Seed data cleared');
    });

  const doDeleteEverything = () =>
    guard('Delete', async () => {
      await destroyDb();
      setRuns([]);
      setSeed(null);
      setStatus('Database deleted — reload the page to start again');
    });

  const busy = phase === 'seeding' || phase === 'running';

  // The `.app` wrapper belongs to the shell, so both harnesses share one column and one set of
  // safe-area insets.
  return (
    <>
      <h1>Touchline U-0 — tap→commit latency</h1>
      <p className="sub">
        Measures the two-row IndexedDB <code>readwrite</code> the design’s tap path performs
        (§5.1), under warm, cold and thermally-loaded conditions, and probes{' '}
        <code>storage.estimate()</code> (BAK-3). This settles assumption A-1 — and only a run on the
        target iPhone, from the home-screen icon, settles it.
      </p>

      {error && <div className="error">{error}</div>}

      {/* Above the controls on purpose. Relaunching at a venue must show the verdict without a
          scroll — "is this evidence" and "does F-9 fire" are the two facts the run exists for. */}
      {report && <ReportHeader report={report} />}

      {notice && (
        <div className="card">
          <strong>{notice}</strong>
        </div>
      )}

      {busy && (
        <div className="card">
          <strong>{status}</strong>
          <div className="progress">
            <div style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <button
            className="danger"
            onClick={() => {
              stopRef.current = true;
              setStatus('Stopping after the current trial…');
            }}
          >
            Stop
          </button>
        </div>
      )}

      {phase === 'liveTap' && tapTarget && (
        <LiveTapPad
          hypothesisId={tapTarget.hypothesisId}
          matchId={tapTarget.matchId}
          onTap={() => tapLeaseRef.current?.observeForeignLoad()}
          onComplete={finishLiveTap}
          onCancel={cancelLiveTap}
        />
      )}

      {!busy && phase !== 'liveTap' && (
        <>
          <div className="card">
            <h2>1 · Seed</h2>
            <p className="sub">
              {seed && seed.observationRows > 0
                ? `${seed.observationRows.toLocaleString()} observations · ${seed.hypothesisRows.toLocaleString()} hypotheses in the database.`
                : 'The database is empty. A commit into an empty store is not the transaction the product performs — seed before measuring.'}
            </p>
            <label className="field">
              Database size
              <select
                value={seedPreset}
                onChange={(e) => setSeedPreset(e.target.value as keyof typeof SEED_PRESETS)}
              >
                {Object.keys(SEED_PRESETS).map((k) => (
                  <option key={k} value={k}>
                    {SEED_LABELS[k] ?? k}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Audio bulk (MB) — makes the BAK-3 estimate meaningful; slow to write
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={500}
                value={audioBulkMb}
                onChange={(e) => setAudioBulkMb(Math.max(0, Number(e.target.value) || 0))}
              />
            </label>
            <button onClick={() => void doSeed()}>Seed database</button>
          </div>

          <div className="card">
            <h2>2 · Measure</h2>
            <label className="field">
              Trials per run ({config.HARNESS_TRIALS_MIN}–{config.HARNESS_TRIALS_MAX})
              <input
                type="number"
                inputMode="numeric"
                min={config.HARNESS_TRIALS_MIN}
                max={config.HARNESS_TRIALS_MAX}
                value={trials}
                onChange={(e) => setTrials(Number(e.target.value) || config.HARNESS_TRIALS_DEFAULT)}
              />
            </label>
            <label className="field">
              Tap pacing
              <select value={pacing} onChange={(e) => setPacing(e.target.value as Pacing)}>
                <option value="burst">Burst — frame-paced, connection stays hot</option>
                <option value="realistic">
                  Realistic — {config.HARNESS_PACING_REALISTIC_MS} ms apart, connection idles
                </option>
              </select>
            </label>
            <label className="field">
              Thermal soak (seconds) — set 0 at a venue, when the phone is already hot
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={config.HARNESS_THERMAL_SOAK_MAX_S}
                value={soakSeconds}
                onChange={(e) => setSoakSeconds(Math.max(0, Number(e.target.value) || 0))}
              />
            </label>
            <div className="row three">
              <button className="primary" onClick={() => void doRun('warm')}>
                Warm
              </button>
              <button onClick={() => void doRun('cold')}>Cold</button>
              <button onClick={() => void doRun('thermal')}>Thermal</button>
            </div>
            <button onClick={() => void startLiveTap()}>Live taps — the run NFR-1 is judged on</button>
          </div>
        </>
      )}

      {report && <ResultsView report={report} runs={runs} />}

      {!busy && (
        <div className="card">
          <h2>3 · Get it off the phone</h2>
          <div className="row two">
            <button className="primary" onClick={() => void doExport()}>
              Share
            </button>
            <button onClick={() => void doCopy()}>Copy text</button>
          </div>
          <button onClick={() => void doDownload()}>Download JSON</button>
          <p className="note">
            {runs.length} run(s) stored on this device. They survive the app being closed.
          </p>
        </div>
      )}

      {!busy && (
        <div className="card">
          <h2>Utilities</h2>
          <div className="row two">
            <button onClick={() => void doProbeOnly()}>Storage probe only</button>
            <button
              onClick={() => {
                if (runs[0]) void guard('Delete run', async () => {
                  await deleteRun(runs[0]!.id);
                  await refresh();
                });
              }}
              disabled={runs.length === 0}
            >
              Delete latest run
            </button>
          </div>
          <div className="row two">
            <button className="danger" onClick={() => void doClearData()}>
              Clear seed data
            </button>
            <button className="danger" onClick={() => void doDeleteEverything()}>
              Delete database
            </button>
          </div>
          <p className="note">Status: {status}</p>
        </div>
      )}
    </>
  );
}
